import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, Menu, X, Phone, Mail, User, LogOut, ChevronDown } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import Footer from "@/components/Footer";
import { SosLogo } from "@/components/SosLogo";

const NAV = [
    { to: "/loja/toners-compativeis", label: "Toners Compatíveis" },
    { to: "/loja/toners-originais", label: "Toners Originais" },
    { to: "/loja/tinteiros", label: "Tinteiros" },
    { to: "/loja/impressoras-recondicionadas", label: "Impressoras" },
    { to: "/loja/computadores-recondicionados", label: "Computadores" },
    { to: "/servicos", label: "Serviços" },
    { to: "/loja/assistencia-tecnica", label: "Assistência" },
];

export default function PublicLayout() {
    const { count } = useCart();
    const { user, logout, isAdmin } = useAuth();
    const [open, setOpen] = useState(false);
    const [userMenu, setUserMenu] = useState(false);
    const [q, setQ] = useState("");
    const [settings, setSettings] = useState(null);
    const nav = useNavigate();
    const userMenuRef = useRef(null);

    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        const onClickOut = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenu(false);
        };
        document.addEventListener("mousedown", onClickOut);
        return () => document.removeEventListener("mousedown", onClickOut);
    }, []);

    const onSearch = (e) => {
        e.preventDefault();
        if (!q.trim()) return;
        nav(`/loja?q=${encodeURIComponent(q)}`);
        setOpen(false);
    };

    const phone = settings?.contact_phone || "+351 000 000 000";
    const email = settings?.contact_email || "geral@sostoners.pt";

    return (
        <div className="min-h-screen flex flex-col bg-paper text-ink" data-testid="public-layout">
            <div className="bg-ink text-white text-xs uppercase tracking-[0.2em] py-2 font-mono" data-testid="announcement-bar">
                <div className="container-edge flex items-center justify-between gap-4">
                    <span className="hidden md:inline">
                        Envio grátis acima de 50€ • Stock dinâmico actualizado
                    </span>
                    <span className="md:hidden">Envio grátis acima de 50€</span>
                    <span className="flex items-center gap-4">
                        <a href={`tel:${phone}`} className="hover:text-magenta inline-flex items-center gap-1">
                            <Phone size={12} /> {phone}
                        </a>
                        <a href={`mailto:${email}`} className="hidden sm:inline-flex items-center gap-1 hover:text-magenta">
                            <Mail size={12} /> {email}
                        </a>
                    </span>
                </div>
            </div>

            <header className="sticky top-0 z-40 bg-paper border-b-2 border-ink" data-testid="site-header">
                <div className="container-edge py-4 flex items-center gap-6">
                    <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="brand-link">
                        <SosLogo size={56} customLogoUrl={settings?.logo_url} />
                    </Link>

                    <form onSubmit={onSearch} className="hidden md:flex flex-1" data-testid="search-form">
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Procurar por modelo, marca ou referência…"
                            className="input-edge flex-1 border-r-0"
                            data-testid="search-input"
                        />
                        <button
                            type="submit"
                            className="bg-ink text-white px-5 border-2 border-ink hover:bg-magenta hover:border-magenta transition-colors"
                            data-testid="search-btn"
                        >
                            <Search size={18} />
                        </button>
                    </form>

                    {/* user / login */}
                    <div className="relative" ref={userMenuRef}>
                        {user ? (
                            <button
                                onClick={() => setUserMenu(!userMenu)}
                                className="flex items-center gap-2 border-2 border-ink px-3 py-2 hover:bg-ink hover:text-white transition-colors font-bold"
                                data-testid="user-menu-btn"
                            >
                                <User size={16} />
                                <span className="hidden sm:inline uppercase tracking-wider text-sm max-w-[100px] truncate">
                                    {user.name?.split(" ")[0] || "Conta"}
                                </span>
                                <ChevronDown size={14} />
                            </button>
                        ) : (
                            <Link
                                to="/conta/login"
                                className="flex items-center gap-2 border-2 border-ink px-3 py-2 hover:bg-ink hover:text-white transition-colors font-bold"
                                data-testid="login-link"
                            >
                                <User size={16} />
                                <span className="hidden sm:inline uppercase tracking-wider text-sm">Entrar</span>
                            </Link>
                        )}
                        {userMenu && user && (
                            <div className="absolute right-0 top-full mt-1 bg-white border-2 border-ink min-w-[200px] z-50 shadow-[6px_6px_0_0_#0A0A0A]" data-testid="user-menu">
                                {isAdmin ? (
                                    <Link
                                        to="/admin"
                                        onClick={() => setUserMenu(false)}
                                        className="block px-4 py-3 border-b-2 border-ink hover:bg-magenta hover:text-white text-sm font-bold uppercase tracking-wider"
                                    >
                                        Painel Admin
                                    </Link>
                                ) : (
                                    <>
                                        <Link
                                            to="/conta"
                                            onClick={() => setUserMenu(false)}
                                            className="block px-4 py-3 border-b-2 border-ink hover:bg-magenta hover:text-white text-sm font-bold uppercase tracking-wider"
                                        >
                                            Minha Conta
                                        </Link>
                                        <Link
                                            to="/conta"
                                            onClick={() => setUserMenu(false)}
                                            className="block px-4 py-3 border-b-2 border-ink hover:bg-magenta hover:text-white text-sm font-bold uppercase tracking-wider"
                                        >
                                            Encomendas
                                        </Link>
                                    </>
                                )}
                                <button
                                    onClick={() => { logout(); setUserMenu(false); nav("/"); }}
                                    className="w-full flex items-center gap-2 px-4 py-3 hover:bg-ink hover:text-white text-sm font-bold uppercase tracking-wider"
                                    data-testid="user-menu-logout"
                                >
                                    <LogOut size={14} /> Sair
                                </button>
                            </div>
                        )}
                    </div>

                    <Link
                        to="/carrinho"
                        className="relative inline-flex items-center gap-2 border-2 border-ink px-4 py-2 hover:bg-ink hover:text-white transition-colors font-bold"
                        data-testid="cart-link"
                    >
                        <ShoppingBag size={18} />
                        <span className="hidden sm:inline uppercase tracking-wider text-sm">Carrinho</span>
                        {count > 0 && (
                            <span
                                className="absolute -top-2 -right-2 bg-magenta text-white text-[10px] font-mono font-bold w-6 h-6 flex items-center justify-center border-2 border-ink"
                                data-testid="cart-badge"
                            >
                                {count}
                            </span>
                        )}
                    </Link>

                    <button
                        className="md:hidden border-2 border-ink p-2"
                        onClick={() => setOpen(!open)}
                        data-testid="menu-toggle"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>

                <nav className="hidden md:block border-t-2 border-ink bg-white">
                    <div className="container-edge flex items-center gap-6 lg:gap-8 py-5 overflow-x-auto">
                        {NAV.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                className={({ isActive }) =>
                                    `text-base font-medium uppercase tracking-[0.18em] whitespace-nowrap hover:text-magenta transition-colors ${
                                        isActive ? "text-magenta" : "text-ink"
                                    }`
                                }
                                data-testid={`nav-${item.to.split("/").pop()}`}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </div>
                </nav>

                {open && (
                    <div className="md:hidden border-t-2 border-ink bg-white" data-testid="mobile-menu">
                        <form onSubmit={onSearch} className="p-4 flex border-b-2 border-ink">
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Procurar…" className="input-edge flex-1 border-r-0" />
                            <button className="bg-ink text-white px-4 border-2 border-ink"><Search size={18} /></button>
                        </form>
                        <div className="flex flex-col">
                            {NAV.map((item) => (
                                <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="px-6 py-4 border-b border-ink/10 text-sm font-bold uppercase tracking-wider hover:text-magenta">
                                    {item.label}
                                </Link>
                            ))}
                            {!user && (
                                <Link to="/conta/login" onClick={() => setOpen(false)} className="px-6 py-4 border-b border-ink/10 text-sm font-bold uppercase tracking-wider text-magenta">
                                    Entrar / Registar
                                </Link>
                            )}
                        </div>
                    </div>
                )}
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            <Footer settings={settings} />
        </div>
    );
}
